import type { Ref } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, EditableToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * FloorPlanDetailsShortcode — renders the RentCafe RRAC floor plan details
 * embed target (`<div class="rrac_floorplanDetails"></div>`) for the
 * Floor Plans Details inner page. The RRAC third-party script hydrates
 * this div at runtime with the floor plan detail widget.
 *
 * A single optional heading token is exposed so site editors can add a
 * label above the embed if desired (hidden when empty).
 */
export function FloorPlanDetailsShortcode() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const heading = useContentToken('floorplandetailsshortcode.heading');

  return (
    <section
      ref={sectionRef as Ref<HTMLElement>}
      className="reveal-fade-up py-10 md:py-14 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {heading ? (
          <EditableToken
            path="floorplandetailsshortcode.heading"
            as="h2"
            className="mb-6 text-center font-heading text-2xl font-normal text-[var(--vibe-color-text)] sm:text-3xl"
          >
            {heading}
          </EditableToken>
        ) : null}

        {/* RentCafe RRAC floor plan details embed target — hydrated by
            the external RRAC script at runtime. */}
        <div className="rrac_floorplanDetails"></div>
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <EditableToken
        path="floorplandetailsshortcode.heading"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlanDetailsShortcode',
  displayName: 'Floor Plan Details Shortcode',
  tokens: {
    'floorplandetailsshortcode.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: '',
      description:
        'Optional heading rendered above the RRAC floor plan details embed. Leave blank to hide.',
    },
  },
};

(FloorPlanDetailsShortcode as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FloorPlanDetailsShortcode;
